import React, { useEffect, useState } from "react";
import { Card } from "./ui/card";
import { api } from "../lib/api";
import { Trophy, Users, Loader2, Copy, Check, IndianRupee } from "lucide-react";
import { toast } from "sonner";

// ---------------------------------------------------------------------------
// ReferralLeaderboard — admin widget surfacing top P2P advocates.
// Fetches /api/admin/referrals/leaderboard and shows a compact ranking
// with contact info + earned credit so ops can send a personal thank-you.
// ---------------------------------------------------------------------------
export default function ReferralLeaderboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedCode, setCopiedCode] = useState(null);

  useEffect(() => {
    api.get("/admin/referrals/leaderboard?limit=15")
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [] }))
      .finally(() => setLoading(false));
  }, []);

  const copy = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      toast.success(`Copied ${code}`);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (_e) {
      toast.error("Copy failed");
    }
  };

  return (
    <Card className="p-6 border-slate-200 bg-white" data-testid="admin-referral-leaderboard">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-600 flex items-center gap-1.5">
            <Trophy className="w-3.5 h-3.5"/> Referral leaderboard
          </div>
          <h3 className="mt-1 font-heading text-xl font-semibold text-slate-900">
            Top patient advocates
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Patients who&apos;ve driven the most new bookings via their referral code.
          </p>
        </div>
        {data && (
          <div className="flex items-center gap-4 text-xs text-slate-600" data-testid="referral-totals">
            <span className="inline-flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5"/>
              <b className="text-slate-900">{data.total_referrers}</b> total referrers
            </span>
            <span className="inline-flex items-center gap-1.5">
              <IndianRupee className="w-3.5 h-3.5"/>
              <b className="text-slate-900">{data.total_paid_conversions}</b> paid conversions
            </span>
          </div>
        )}
      </div>

      {loading ? (
        <div className="mt-6 flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-slate-400 animate-spin"/>
        </div>
      ) : !data?.items?.length ? (
        <div className="mt-6 rounded-lg border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500" data-testid="leaderboard-empty">
          No referral activity yet — the first advocate will appear here once
          they successfully refer a paying patient.
        </div>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="text-left py-2 pr-3">Rank</th>
                <th className="text-left py-2 pr-3">Patient</th>
                <th className="text-left py-2 pr-3">Code</th>
                <th className="text-right py-2 pr-3">Referred</th>
                <th className="text-right py-2 pr-3">Paid</th>
                <th className="text-right py-2 pr-3">Earned</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((it, idx) => (
                <tr
                  key={it.referrer_id}
                  className="border-b border-slate-100 hover:bg-slate-50/60"
                  data-testid={`leaderboard-row-${idx}`}
                >
                  <td className="py-2.5 pr-3">
                    <span className={`inline-flex w-7 h-7 rounded-full items-center justify-center text-xs font-bold ${
                      idx === 0 ? "bg-amber-100 text-amber-700 ring-1 ring-amber-200" :
                      idx === 1 ? "bg-slate-200 text-slate-700" :
                      idx === 2 ? "bg-orange-100 text-orange-700" :
                      "bg-slate-100 text-slate-600"
                    }`}>{idx + 1}</span>
                  </td>
                  <td className="py-2.5 pr-3">
                    <div className="font-semibold text-slate-900">{it.referrer_name}</div>
                    <div className="text-[11px] text-slate-500">
                      {it.referrer_phone || it.referrer_email || "—"}
                    </div>
                  </td>
                  <td className="py-2.5 pr-3">
                    <button
                      type="button"
                      onClick={() => copy(it.referrer_code)}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded font-mono text-xs font-bold bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-100"
                      data-testid={`leaderboard-code-${idx}`}
                    >
                      {it.referrer_code}
                      {copiedCode === it.referrer_code
                        ? <Check className="w-3 h-3"/>
                        : <Copy className="w-3 h-3 opacity-60"/>}
                    </button>
                  </td>
                  <td className="py-2.5 pr-3 text-right font-semibold text-slate-900">{it.total_referred}</td>
                  <td className="py-2.5 pr-3 text-right font-semibold text-emerald-700">{it.paid_referred}</td>
                  <td className="py-2.5 pr-3 text-right font-semibold text-slate-900">₹{it.total_earned_rupees.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
